import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion, useMotionValue, useTransform, AnimatePresence } from 'framer-motion';

const CountUp = ({
  end,
  suffix = '',
  duration = 2.5,
  inView,
}: {
  end: number;
  suffix?: string;
  duration?: number;
  inView: boolean;
}) => {
  const [count, setCount] = useState(0);
  const hasAnimated = useRef(false);

  useEffect(() => {
    if (!inView || hasAnimated.current) return;
    hasAnimated.current = true;
    const startTime = Date.now();
    const isDecimal = end % 1 !== 0;

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / (duration * 1000), 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = eased * end;
      setCount(isDecimal ? parseFloat(current.toFixed(1)) : Math.floor(current));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [inView, end, duration]);

  return (
    <span>
      {end % 1 !== 0 ? count.toFixed(1) : count.toLocaleString('tr-TR')}
      {suffix}
    </span>
  );
};

const productData = [
  {
    name: 'PET Şişe',
    icon: 'ri-drop-line',
    x: 'right-[8%]',
    y: 'top-[22%]',
    delay: 0.3,
    image: 'https://public.readdy.ai/ai/img_res/29ae264a2571020225c7b52224f81d19.jpg',
    tag: 'Gıda Temasına Uygun',
    desc: 'ISO 22000 & FDA sertifikalı, 50ml – 5L arası çeşitli hacimler',
    specs: ['BPA Free', 'Gıda Onaylı', 'Geri Dönüştürülebilir'],
    color: 'from-teal-500/20 to-green-500/20',
    accent: '#4ade80',
  },
  {
    name: 'PET Kavanoz',
    icon: 'ri-archive-line',
    x: 'right-[5%]',
    y: 'top-[48%]',
    delay: 0.5,
    image: 'https://readdy.ai/api/search-image?query=premium%20clear%20PET%20plastic%20jar%20container%20product%20photography%20on%20deep%20navy%20blue%20dark%20background%2C%20studio%20lighting%2C%20wide%20mouth%20transparent%20jar%20with%20lid%2C%20professional%20commercial%20photography%2C%20ultra%20sharp%2C%20cinematic%20lighting%2C%20luxury%20packaging%20design&width=400&height=500&seq=petjar02&orientation=portrait',
    tag: 'Geniş Ağızlı Tasarım',
    desc: 'Kozmetik, gıda ve endüstriyel kullanım için ideal',
    specs: ['Şeffaf', 'Dayanıklı', 'Özel Boyut'],
    color: 'from-cyan-500/20 to-teal-500/20',
    accent: '#22d3ee',
  },
  {
    name: 'PP Kapak',
    icon: 'ri-shield-check-line',
    x: 'right-[12%]',
    y: 'top-[72%]',
    delay: 0.7,
    image: 'https://readdy.ai/api/search-image?query=premium%20polypropylene%20PP%20plastic%20caps%20lids%20closures%20product%20photography%20on%20deep%20navy%20blue%20dark%20background%2C%20studio%20lighting%2C%20multiple%20colorful%20caps%20arranged%20elegantly%2C%20professional%20commercial%20photography%2C%20ultra%20sharp%2C%20cinematic%20lighting%2C%20luxury%20packaging&width=400&height=500&seq=ppcap03&orientation=portrait',
    tag: 'Sızdırmaz Sistem',
    desc: 'Tüm PET şişe ve kavanozlarla uyumlu kapak çözümleri',
    specs: ['Sızdırmaz', 'Çok Renkli', 'Özel Baskı'],
    color: 'from-emerald-500/20 to-cyan-500/20',
    accent: '#34d399',
  },
];

const HeroBanner = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const [isLoaded, setIsLoaded] = useState(false);
  const [activeSlide, setActiveSlide] = useState(0);
  const [hoveredProduct, setHoveredProduct] = useState<number | null>(null);

  const bgX = useTransform(mouseX, [0, 1], [-5, 5]);
  const bgY = useTransform(mouseY, [0, 1], [-5, 5]);

  const slides = useMemo(
    () => [
      {
        tag: 'Anadolu Pet Ambalaj Güvencesiyle',
        title: 'Ambalajda Güven,',
        titleLine2: 'Üretimde Mükemmellik',
        desc: 'Türkiye merkezli, yüksek kaliteli PET ambalaj üretimi ve ihracatı. Avrupa standartlarında, global pazara hizmet.',
      },
      {
        tag: 'Avrupa Standartlarında Kalite',
        title: 'Modern Teknoloji,',
        titleLine2: 'Güvenilir Üretim',
        desc: 'ISO 9001, ISO 22000 ve FDA sertifikalı üretim tesislerimizde gıda temasına uygun ambalaj üretimi.',
      },
      {
        tag: 'Sürdürülebilir Gelecek',
        title: '%100 Geri',
        titleLine2: 'Dönüştürülebilir',
        desc: 'Çevre dostu üretim süreçlerimizle doğaya saygılı, sürdürülebilir ambalaj çözümleri.',
      },
    ],
    []
  );

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % slides.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [slides.length]);

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      mouseX.set((e.clientX - rect.left) / rect.width);
      mouseY.set((e.clientY - rect.top) / rect.height);
    },
    [mouseX, mouseY]
  );

  const stats = [
    { number: 4000, suffix: '+', label: 'Günlük Üretim', icon: 'ri-settings-3-line' },
    { number: 20, suffix: '+', label: 'İhracat Ülkesi', icon: 'ri-global-line' },
    { number: 99.8, suffix: '%', label: 'Kalite Skoru', icon: 'ri-shield-star-line' },
  ];

  const activeProduct = hoveredProduct !== null ? productData[hoveredProduct] : null;

  return (
    <section
      ref={containerRef}
      onMouseMove={handleMouseMove}
      className="relative min-h-screen flex items-center overflow-hidden"
    >
      {/* Background Image with Parallax */}
      <motion.div style={{ x: bgX, y: bgY }} className="absolute inset-[-20px]">
        <video
          src="https://public.readdy.ai/ai/video_res/ccb3fc2e-af99-4266-9df8-d16dd894faca.mp4"
          autoPlay
          loop
          muted
          playsInline
          className="w-full h-full object-cover object-top scale-105"
        />
      </motion.div>

      {/* Dark Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-navy/95 via-navy/85 to-navy/60" />
      <div className="absolute inset-0 bg-gradient-to-t from-navy/90 via-transparent to-navy/40" />

      {/* Soft Glowing Orbs */}
      <motion.div
        animate={{ x: [0, 15, -10, 0], y: [0, -10, 15, 0], scale: [1, 1.08, 0.95, 1] }}
        transition={{ duration: 20, repeat: Infinity, ease: 'easeInOut' }}
        className="absolute top-[15%] right-[20%] w-72 h-72 bg-green/8 rounded-full blur-[120px] z-[1]"
      />
      <motion.div
        animate={{ x: [0, -20, 10, 0], y: [0, 15, -10, 0], scale: [1, 0.92, 1.05, 1] }}
        transition={{ duration: 25, repeat: Infinity, ease: 'easeInOut' }}
        className="absolute bottom-[20%] left-[10%] w-96 h-96 bg-teal/6 rounded-full blur-[140px] z-[1]"
      />

      {/* Main Content */}
      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 pt-24 sm:pt-28 pb-12 sm:pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center min-h-[calc(100vh-160px)]">
          {/* Left Content */}
          <div className="lg:col-span-7 xl:col-span-7">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={isLoaded ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.2, ease: 'easeOut' }}
              className="mb-6 sm:mb-8"
            >
              <span className="inline-flex items-center gap-2.5 px-5 py-2.5 bg-white/10 border border-white/20 rounded-full backdrop-blur-md">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green opacity-75" />
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green" />
                </span>
                <AnimatePresence mode="wait">
                  <motion.span
                    key={activeSlide}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.4, ease: 'easeOut' }}
                    className="text-white/90 text-xs sm:text-sm font-medium tracking-wide"
                  >
                    {slides[activeSlide].tag}
                  </motion.span>
                </AnimatePresence>
              </span>
            </motion.div>

            <div className="mb-6 sm:mb-8 overflow-hidden">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeSlide}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.6, ease: 'easeOut' }}
                >
                  <motion.h1
                    initial={{ opacity: 0, y: 40 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.1, ease: 'easeOut' }}
                    className="text-4xl sm:text-5xl md:text-6xl lg:text-6xl xl:text-7xl font-bold text-white leading-[1.1] mb-2"
                  >
                    {slides[activeSlide].title}
                  </motion.h1>
                  <motion.h1
                    initial={{ opacity: 0, y: 40 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.2, ease: 'easeOut' }}
                    className="text-4xl sm:text-5xl md:text-6xl lg:text-6xl xl:text-7xl font-bold leading-[1.1]"
                  >
                    <span className="bg-gradient-to-r from-green via-teal to-green bg-clip-text text-transparent">
                      {slides[activeSlide].titleLine2}
                    </span>
                  </motion.h1>
                </motion.div>
              </AnimatePresence>
            </div>

            <AnimatePresence mode="wait">
              <motion.p
                key={activeSlide}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -15 }}
                transition={{ duration: 0.6, delay: 0.3, ease: 'easeOut' }}
                className="text-base sm:text-lg text-white/80 mb-8 sm:mb-10 max-w-xl leading-relaxed"
              >
                {slides[activeSlide].desc}
              </motion.p>
            </AnimatePresence>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isLoaded ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.5, ease: 'easeOut' }}
              className="flex flex-col sm:flex-row items-start gap-4 mb-12 sm:mb-16"
            >
              <Link
                to="/products"
                className="group relative px-8 sm:px-10 py-3.5 sm:py-4 bg-gradient-to-r from-green to-teal text-white text-sm sm:text-base font-semibold rounded-lg overflow-hidden transition-all duration-500 shadow-lg shadow-green/25 hover:shadow-xl hover:shadow-green/30 hover:-translate-y-0.5 whitespace-nowrap cursor-pointer"
              >
                <span className="relative z-10 flex items-center gap-2">
                  Ürünlerimizi Keşfedin
                  <motion.i
                    animate={{ x: [0, 3, 0] }}
                    transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                    className="ri-arrow-right-line text-lg"
                  />
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-teal to-green opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              </Link>
              <Link
                to="/contact"
                className="group px-8 sm:px-10 py-3.5 sm:py-4 bg-white/5 border border-white/30 text-white text-sm sm:text-base font-semibold rounded-lg hover:bg-white/15 hover:border-white/50 hover:-translate-y-0.5 transition-all duration-500 whitespace-nowrap cursor-pointer backdrop-blur-sm"
              >
                <span className="flex items-center gap-2">
                  <i className="ri-phone-line text-green" />
                  Teklif Alın
                </span>
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isLoaded ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.7, ease: 'easeOut' }}
              className="flex flex-wrap gap-4 sm:gap-6 lg:gap-8"
            >
              {stats.map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={isLoaded ? { opacity: 1, scale: 1 } : {}}
                  transition={{ duration: 0.6, delay: 0.8 + index * 0.1, ease: 'easeOut' }}
                  className="group flex items-center gap-3 sm:gap-4 bg-white/5 backdrop-blur-md border border-white/10 rounded-xl px-5 sm:px-6 py-3.5 sm:py-4 hover:bg-white/10 hover:border-green/30 transition-all duration-500 cursor-default"
                >
                  <div className="w-10 h-10 sm:w-11 sm:h-11 flex items-center justify-center bg-green/20 rounded-lg group-hover:bg-green/30 transition-colors duration-500">
                    <i className={`${stat.icon} text-green text-lg sm:text-xl`} />
                  </div>
                  <div>
                    <div className="text-xl sm:text-2xl font-bold text-white">
                      <CountUp end={stat.number} suffix={stat.suffix} inView={isLoaded} />
                    </div>
                    <div className="text-white/60 text-xs sm:text-sm font-medium">{stat.label}</div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>

          {/* Right Side - Floating Product Cards + Hover Preview */}
          <div className="hidden lg:flex lg:col-span-5 xl:col-span-5 relative items-center justify-center">
            <div className="relative w-full h-[500px]">
              {/* Central Glow */}
              <motion.div
                animate={{ scale: [1, 1.1, 1], opacity: [0.25, 0.4, 0.25] }}
                transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-56 h-56 bg-green/15 rounded-full blur-[100px]"
              />

              {/* Subtle Ring */}
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 60, repeat: Infinity, ease: 'linear' }}
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 h-80 border border-white/5 rounded-full"
              />

              {/* ── HOVER PREVIEW PANEL ── */}
              <AnimatePresence>
                {activeProduct && (
                  <motion.div
                    key={activeProduct.name}
                    initial={{ opacity: 0, x: 40, scale: 0.92, rotateY: -8 }}
                    animate={{ opacity: 1, x: 0, scale: 1, rotateY: 0 }}
                    exit={{ opacity: 0, x: 30, scale: 0.94, rotateY: 8 }}
                    transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                    style={{ perspective: 800 }}
                    className="absolute left-0 top-1/2 -translate-y-1/2 w-[220px] z-30 pointer-events-none"
                  >
                    {/* Glow behind card */}
                    <motion.div
                      animate={{ opacity: [0.5, 0.9, 0.5], scale: [1, 1.08, 1] }}
                      transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
                      className={`absolute inset-0 rounded-2xl blur-2xl bg-gradient-to-br ${activeProduct.color}`}
                    />

                    <div className="relative rounded-2xl overflow-hidden border border-white/20 shadow-2xl shadow-black/40 backdrop-blur-xl bg-white/5">
                      {/* Image */}
                      <div className="relative w-full h-[200px] overflow-hidden">
                        <motion.img
                          key={activeProduct.image}
                          initial={{ scale: 1.12, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          transition={{ duration: 0.6, ease: 'easeOut' }}
                          src={activeProduct.image}
                          alt={activeProduct.name}
                          className="w-full h-full object-cover object-top"
                        />
                        {/* Image overlay gradient */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />

                        {/* Shimmer sweep */}
                        <motion.div
                          animate={{ x: ['-100%', '200%'] }}
                          transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut', repeatDelay: 1.5 }}
                          className="absolute inset-0 bg-gradient-to-r from-transparent via-white/15 to-transparent skew-x-12"
                        />

                        {/* Tag badge */}
                        <div className="absolute bottom-3 left-3">
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-black/50 backdrop-blur-md border border-white/20 rounded-full text-[10px] text-white/90 font-medium">
                            <span
                              className="w-1.5 h-1.5 rounded-full animate-pulse"
                              style={{ backgroundColor: activeProduct.accent }}
                            />
                            {activeProduct.tag}
                          </span>
                        </div>
                      </div>

                      {/* Info */}
                      <div className="p-4">
                        <div className="flex items-center gap-2 mb-1.5">
                          <div
                            className="w-7 h-7 flex items-center justify-center rounded-lg"
                            style={{ backgroundColor: `${activeProduct.accent}22` }}
                          >
                            <i
                              className={`${activeProduct.icon} text-sm`}
                              style={{ color: activeProduct.accent }}
                            />
                          </div>
                          <span className="text-white font-semibold text-sm">{activeProduct.name}</span>
                        </div>
                        <p className="text-white/60 text-[11px] leading-relaxed mb-3">{activeProduct.desc}</p>

                        {/* Spec pills */}
                        <div className="flex flex-wrap gap-1.5">
                          {activeProduct.specs.map((spec, i) => (
                            <motion.span
                              key={spec}
                              initial={{ opacity: 0, y: 6 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: i * 0.07, duration: 0.3 }}
                              className="px-2 py-0.5 rounded-full text-[10px] font-medium border"
                              style={{
                                color: activeProduct.accent,
                                borderColor: `${activeProduct.accent}40`,
                                backgroundColor: `${activeProduct.accent}12`,
                              }}
                            >
                              {spec}
                            </motion.span>
                          ))}
                        </div>

                        {/* Bottom CTA line */}
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: '100%' }}
                          transition={{ duration: 0.5, delay: 0.2 }}
                          className="mt-3 pt-3 border-t border-white/10 flex items-center justify-between"
                        >
                          <span className="text-[11px] text-white/50">Detayları gör</span>
                          <motion.i
                            animate={{ x: [0, 4, 0] }}
                            transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
                            className="ri-arrow-right-line text-sm"
                            style={{ color: activeProduct.accent }}
                          />
                        </motion.div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Floating Product Cards */}
              {productData.map((product, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.8, y: 20 }}
                  animate={isLoaded ? { opacity: 1, scale: 1, y: [0, -8, 0] } : {}}
                  transition={{
                    opacity: { duration: 0.8, delay: product.delay, ease: 'easeOut' },
                    scale: { duration: 0.8, delay: product.delay, ease: 'easeOut' },
                    y: { duration: 5 + index, repeat: Infinity, ease: 'easeInOut' },
                  }}
                  className={`absolute ${product.x} ${product.y} z-20`}
                  onMouseEnter={() => setHoveredProduct(index)}
                  onMouseLeave={() => setHoveredProduct(null)}
                >
                  <motion.div
                    animate={
                      hoveredProduct === index
                        ? { scale: 1.08, borderColor: product.accent }
                        : { scale: 1 }
                    }
                    transition={{ duration: 0.3, ease: 'easeOut' }}
                    className="group bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-5 sm:p-6 transition-all duration-300 cursor-pointer shadow-xl shadow-black/10 min-w-[160px] relative overflow-hidden"
                    style={
                      hoveredProduct === index
                        ? { boxShadow: `0 0 30px ${product.accent}30, 0 8px 32px rgba(0,0,0,0.3)` }
                        : {}
                    }
                  >
                    {/* Shimmer on hover */}
                    {hoveredProduct === index && (
                      <motion.div
                        initial={{ x: '-100%' }}
                        animate={{ x: '200%' }}
                        transition={{ duration: 0.8, ease: 'easeOut' }}
                        className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent skew-x-12 pointer-events-none"
                      />
                    )}

                    <div
                      className="w-12 h-12 flex items-center justify-center rounded-xl mb-3 transition-transform duration-500 group-hover:scale-110"
                      style={{ backgroundColor: `${product.accent}25` }}
                    >
                      <i
                        className={`${product.icon} text-2xl`}
                        style={{ color: product.accent }}
                      />
                    </div>
                    <div className="text-white font-semibold text-sm">{product.name}</div>
                    <div className="flex items-center gap-1 mt-2 text-xs font-medium" style={{ color: `${product.accent}cc` }}>
                      <span>Keşfet</span>
                      <motion.i
                        animate={hoveredProduct === index ? { x: [0, 4, 0] } : { x: 0 }}
                        transition={{ duration: 0.8, repeat: hoveredProduct === index ? Infinity : 0 }}
                        className="ri-arrow-right-s-line text-sm"
                      />
                    </div>

                    {/* Active indicator dot */}
                    {hoveredProduct === index && (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className="absolute top-3 right-3 w-2 h-2 rounded-full animate-pulse"
                        style={{ backgroundColor: product.accent }}
                      />
                    )}
                  </motion.div>
                </motion.div>
              ))}

              {/* Center Logo/Icon */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={isLoaded ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 1, delay: 0.8, ease: 'easeOut' }}
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10"
              >
                <motion.div
                  animate={{ rotate: [0, 3, -3, 0] }}
                  transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
                  className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-green to-teal rounded-2xl shadow-2xl shadow-green/30"
                >
                  <i className="ri-recycle-line text-white text-4xl" />
                </motion.div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>

      {/* Slide Indicators */}
      <div className="absolute bottom-8 sm:bottom-12 left-1/2 -translate-x-1/2 z-10 flex items-center gap-3">
        {slides.map((_, index) => (
          <button key={index} onClick={() => setActiveSlide(index)} className="group relative cursor-pointer">
            <div
              className={`h-1.5 rounded-full transition-all duration-700 ${
                activeSlide === index ? 'w-10 bg-green' : 'w-4 bg-white/30 group-hover:bg-white/50'
              }`}
            />
          </button>
        ))}
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.2, delay: 1.5 }}
        className="absolute bottom-8 sm:bottom-12 right-8 sm:right-12 z-10 hidden lg:flex flex-col items-center gap-2"
      >
        <span className="text-white/40 text-[10px] uppercase tracking-[3px] rotate-90 origin-center mb-8">Keşfet</span>
        <motion.div
          animate={{ y: [0, 5, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          className="w-6 h-10 border-2 border-white/20 rounded-full flex items-start justify-center p-1.5"
        >
          <motion.div
            animate={{ y: [0, 10, 0], opacity: [1, 0.4, 1] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
            className="w-1.5 h-1.5 bg-green rounded-full"
          />
        </motion.div>
      </motion.div>
    </section>
  );
};

export default HeroBanner;
